import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { all_routes } from '../../router/all_routes';
import { getEmpLeaveTimeAccounts } from '../../../core/data/redux/leaveSlice';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import LeaveModuleTabs from './LeaveModuleTabs';

const LEAVE_COLORS: any = {
    'Casual Leave': { color: '#fbc02d', bg: '#fff9c4' },
    'Earned Leave - Non Encash': { color: '#00bcd4', bg: '#e0f7fa' },
    'Leave on Half Average Pay': { color: '#f44336', bg: '#ffebee' },
    'Earned Leave -Encashable': { color: '#795548', bg: '#efebe9' },
    'Sick Leave': { color: '#f44336', bg: '#ffebee' },
    'Restricted Holidays': { color: '#795548', bg: '#efebe9' },
    'Maternity Leave': { color: '#e91e63', bg: '#fce4ec' },
    'Paternity Leave': { color: '#3f51b5', bg: '#e8eaf6' },
};

const getLeaveColor = (type: string) => {
    return LEAVE_COLORS[type] || { color: '#607d8b', bg: '#eceff1' };
};

const LeaveManagementTimeAccounts = () => {
    const routes = all_routes;
    const location = useLocation();
    const dispatch = useDispatch();

    const [fromDate, setFromDate] = React.useState('');
    const [selectedTypeCode, setSelectedTypeCode] = React.useState('');

    const employeesState = useSelector((state: any) => state.employeesState);
    const empId = (location.state as any)?.overrideEmployeeID || employeesState?.data?.data?.empId || "";

    const leaveState = useSelector((state: any) => state.leavesState);
    const { leaveTimeAccounts, loading } = leaveState || {};

    useEffect(() => {
        if (!empId) {
            dispatch(getMyProfile({}) as any);
        }
    }, [dispatch, empId]);

    const handleFilter = () => {
        if (empId) {
            const payload: any = { empId };
            if (fromDate) payload.fromDate = fromDate;
            if (selectedTypeCode) payload.leaveType = selectedTypeCode;
            dispatch(getEmpLeaveTimeAccounts(payload));
        }
    };

    useEffect(() => {
        if (empId) {
            handleFilter();
        }
    }, [dispatch, empId]);

    const accounts = leaveTimeAccounts?.data || [];

    // Get unique leave types and their codes for filter
    const uniqueTypes = accounts.reduce((acc: any[], current: any) => {
        const x = acc.find(item => item.code === current.TimeAccountTypeCode);
        if (!x) return acc.concat([{ name: current.TimeAccountTypeName, code: current.TimeAccountTypeCode }]);
        return acc;
    }, []);

    // Helper to format /Date(ms)/ to DD.MM.YYYY
    const formatJsonDate = (jsonDate: string) => {
        if (!jsonDate) return '-';
        const timestamp = parseInt(jsonDate.replace(/\/Date\((\d+)\)\//, '$1'));
        if (isNaN(timestamp)) return jsonDate;
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '.');
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <LeaveModuleTabs />

                {/* Modern Web Header Layout - Minimalist */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <Link to={routes.employeeDashboard || '#'} className="text-secondary me-3 text-decoration-none bg-white border d-flex align-items-center justify-content-center shadow-sm" style={{ width: '38px', height: '38px', borderRadius: '50%' }}>
                            <i className="ti ti-arrow-left" style={{ fontSize: '18px' }}></i>
                        </Link>
                        <div>
                            <h4 className="mb-0 fw-bold fs-20" style={{ color: '#e12b2b' }}>Time Accounts</h4>
                            <span className="text-muted fs-12">Leave Entitlements & Balances</span>
                        </div>
                    </div>

                    {/* Minimalist Filter Bar */}
                    <div className="d-flex align-items-center gap-2 bg-white p-2 border shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="d-none d-md-flex align-items-center px-2">
                            <i className="ti ti-filter text-muted me-2 fs-14"></i>
                            <span className="text-muted fs-13 fw-medium">Quick Filter:</span>
                        </div>
                        <div className="d-flex align-items-center bg-light px-2 rounded" style={{ height: '32px' }}>
                            <span className="text-muted fs-11 me-2">from</span>
                            <input
                                type="date"
                                className="form-control form-control-sm border-0 bg-transparent fs-13 shadow-none p-0"
                                style={{ width: '110px' }}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <select
                            className="form-select form-select-sm border-0 bg-light fs-13 shadow-none"
                            style={{ width: '160px', borderRadius: '6px' }}
                            value={selectedTypeCode}
                            onChange={(e) => setSelectedTypeCode(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {uniqueTypes.map((type: any) => (
                                <option key={type.code} value={type.code}>{type.name}</option>
                            ))}
                        </select>
                        <button
                            className="btn btn-sm btn-danger px-3 shadow-none border-0"
                            style={{ height: '32px', backgroundColor: '#e12b2b', borderRadius: '6px' }}
                            onClick={handleFilter}
                        >
                            <i className="ti ti-check fs-14"></i>
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="pb-5">
                    {loading === "pending" && !leaveTimeAccounts ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-danger" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                            <div className="card-header bg-white border-0 pt-4 px-4">
                                <h5 className="fw-bold text-dark mb-0">Time Accounts Overview</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-4">
                                    {accounts.length > 0 ? (
                                        accounts.map((account: any, index: number) => (
                                            <div className="col-12" key={index}>
                                                <div className="p-3 border rounded shadow-none bg-white" style={{ borderColor: '#eee' }}>
                                                    <div className="d-flex flex-column gap-1 text-dark fs-14">
                                                        <div><span className="opacity-75">Deduction From:</span> <span className="fw-medium">{formatJsonDate(account.DeductionStartDate)}</span></div>
                                                        <div><span className="opacity-75">Deduction To:</span> <span className="fw-medium">{formatJsonDate(account.DeductionEndDate)}</span></div>
                                                        <div><span className="opacity-75">Time Account:</span> <span className="fw-medium">{account.TimeAccountTypeName}</span></div>
                                                        <div><span className="opacity-75">Entitlement:</span> <span className="fw-medium">{parseFloat(account.BalanceEntitlementQuantity || 0).toFixed(2)} {account.TimeUnitName || 'Days'}</span></div>
                                                        <div><span className="opacity-75">Entitlement Minus Planned:</span> <span className="fw-bold" style={{ color: '#e12b2b' }}>{parseFloat(account.BalanceAvailableQuantity || 0).toFixed(2)} {account.TimeUnitName || 'Days'}</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="col-12 text-center py-4">
                                            <i className="ti ti-calendar-stats fs-40 text-muted opacity-25 mb-3"></i>
                                            <p className="text-muted fs-14 fw-medium">No active time accounts found.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeaveManagementTimeAccounts;
